import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, ArrowLeft, Check, GraduationCap, Headphones, PenTool, Mic, Landmark, Loader2 } from 'lucide-react';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { Button } from '../components/shared/Button';
import { EmailVerificationPanel } from '../components/shared/EmailVerificationPanel';
import { useAuth } from '../contexts/AuthContext';
import { examService, type Exam } from '../services/exam.service';
import type { Section } from '../config/examSections';
import { SECTIONS as SECTION_IDS, getExamType } from '../config/examSections';

const SECTION_ICONS: Record<Section, React.ReactNode> = {
  Lezen: <BookOpen className="w-6 h-6" />,
  Luisteren: <Headphones className="w-6 h-6" />,
  Schrijven: <PenTool className="w-6 h-6" />,
  Spreken: <Mic className="w-6 h-6" />,
  KNM: <Landmark className="w-6 h-6" />,
};

const SECTION_LABEL_KEY: Record<Section, string> = {
  Lezen: 'reading',
  Luisteren: 'listening',
  Schrijven: 'writing',
  Spreken: 'speaking',
  KNM: 'knm',
};

interface SectionExamState {
  exams: Exam[] | null;
  loading: boolean;
  error: string | null;
}

export const ExamSelectionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialSection = searchParams.get('section') as Section | null;

  const hasPaidAccess = user?.subscription?.status === 'active' || user?.passActive === true;
  const emailVerificationBlocked = user?.emailVerified === false && hasPaidAccess;

  const [selectedSection, setSelectedSection] = useState<Section | null>(initialSection);
  const [examState, setExamState] = useState<SectionExamState>({ exams: null, loading: false, error: null });
  const [selectedExamId, setSelectedExamId] = useState<string | null>(null);

  // Fetch exams when a section is selected
  useEffect(() => {
    if (!selectedSection) {
      setExamState({ exams: null, loading: false, error: null });
      setSelectedExamId(null);
      return;
    }

    let cancelled = false;
    const examType = getExamType(selectedSection);
    setExamState({ exams: null, loading: true, error: null });
    setSelectedExamId(null);

    examService.getExams({ type: examType }).then((exams) => {
      if (cancelled) return;
      setExamState({ exams, loading: false, error: null });
      // Auto-navigate if only one exam
      if (exams.length === 1) {
        navigate(`/pre-exam?section=${selectedSection}&examId=${exams[0]._id}`);
      }
    }).catch((e: any) => {
      if (cancelled) return;
      setExamState({
        exams: null,
        loading: false,
        error: e?.response?.data?.message || t('examSelection.loadError'),
      });
    });

    return () => { cancelled = true; };
  }, [selectedSection, navigate, t]);

  const handleExamContinue = () => {
    if (!selectedSection || !selectedExamId) return;
    navigate(`/pre-exam?section=${selectedSection}&examId=${selectedExamId}`);
  };

  return (
    <div className="page-shell flex min-h-screen flex-col">
      <Header />
      <div className="h-20" />
      <main className="container mx-auto flex-1 max-w-4xl px-4 py-8">
        <button
          onClick={() => selectedSection && !initialSection ? setSelectedSection(null) : navigate('/dashboard')}
          className="mb-6 flex items-center gap-2 text-slate-600 transition-colors hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          {selectedSection && !initialSection ? t('examSelection.previousStep') : t('examSelection.backToDashboard')}
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <GraduationCap className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-ink">{t('examSelection.title')}</h1>
              <p className="text-slate-600">{t('examSelection.subtitle')}</p>
            </div>
          </div>
        </div>

        {emailVerificationBlocked && <EmailVerificationPanel className="mb-6" />}

        {/* Section selection */}
        {!selectedSection && (
          <div className="surface-card mb-6 p-6">
            <h2 className="mb-2 text-2xl font-bold text-ink">{t('examSelection.selectSection')}</h2>
            <p className="mb-6 text-slate-600">{t('examSelection.selectSectionDescription')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {SECTION_IDS.map((s) => (
                <button
                  key={s}
                  onClick={() => setSelectedSection(s)}
                  className="rounded-2xl border border-primary/15 bg-white p-5 text-left transition-all hover:border-primary hover:shadow-soft"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      {SECTION_ICONS[s]}
                    </div>
                  </div>
                  <p className="mb-1 text-lg font-bold text-ink">{t(`examSelection.sections.${SECTION_LABEL_KEY[s]}.label`)}</p>
                  <p className="text-sm text-slate-500">{t(`examSelection.sections.${SECTION_LABEL_KEY[s]}.description`)}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading state */}
        {selectedSection && examState.loading && (
          <div className="surface-card mb-6 flex flex-col items-center gap-3 p-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-slate-600">{t('common.loading')}</p>
          </div>
        )}

        {/* Error state */}
        {selectedSection && examState.error && (
          <div className="mb-6 rounded-2xl border border-error/25 bg-error/10 p-4 text-error">
            <p className="font-medium">{examState.error}</p>
          </div>
        )}

        {/* No exams available */}
        {!emailVerificationBlocked && selectedSection && examState.exams !== null && examState.exams.length === 0 && (
          <div className="mb-6 rounded-2xl border border-warning/30 bg-warning/10 p-4 text-warning">
            <p className="font-medium">{t('examSelection.noExams')}</p>
            <p className="mt-1 text-sm">{t('examSelection.noExamsHint')}</p>
          </div>
        )}

        {/* Exam picker (shown when multiple exams exist) */}
        {selectedSection && examState.exams !== null && examState.exams.length > 1 && (
          <div className="surface-card mb-6 p-6">
            <h3 className="mb-2 font-bold text-ink">
              {t(`examSelection.sections.${SECTION_LABEL_KEY[selectedSection]}.label`)} — {t('examSelection.chooseExam')}
            </h3>
            <p className="mb-4 text-sm text-slate-600">{t('examSelection.chooseExamHint')}</p>
            <div className="space-y-3">
              {examState.exams.map((exam) => {
                const questionLabel = t(selectedSection === 'Schrijven' || selectedSection === 'Spreken' ? 'examSelection.tasks' : 'examSelection.questions');
                return (
                  <button
                    key={exam._id}
                    type="button"
                    onClick={() => setSelectedExamId(exam._id)}
                    className={`w-full rounded-2xl border p-4 text-left transition-all ${
                      selectedExamId === exam._id ? 'border-primary bg-primary/5' : 'border-primary/15 hover:border-primary/45'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-ink">{exam.type === 'knm' && exam.isTrial ? t('knm.trialTitle') : exam.title}</p>
                        <p className="text-sm text-slate-500">
                          {exam.level ? `${exam.level} · ` : ''}{exam.duration} min · {exam.type === 'knm' && !exam.isTrial ? 40 : exam.questionCount ?? 0} {questionLabel}
                          {exam.requiresSubscription && ` · ${t('examSelection.subscriptionRequired')}`}
                        </p>
                      </div>
                      {selectedExamId === exam._id && <Check className="h-5 w-5 shrink-0 text-primary" />}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-4 mt-6">
              <Button variant="tertiary" onClick={() => setSelectedSection(null)} className="flex-1">
                {t('examSelection.previous')}
              </Button>
              <Button
                variant="primary"
                onClick={handleExamContinue}
                disabled={!selectedExamId}
                className="flex-1"
              >
                {t('examSelection.continueToSetup')}
              </Button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ExamSelectionPage;
